import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Header from '../components/layout/Header.jsx';
import {
  useUsuarios,
  useCreateUsuario,
  useUpdateUsuario,
  useToggleActivo,
  useResetPassword,
} from '../features/usuarios/useUsuarios.js';

const crearSchema = z.object({
  nombre: z.string().min(1, 'Requerido'),
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'Mínimo 8 caracteres'),
  rol: z.enum(['vendedor', 'admin', 'viewer']),
});

const editarSchema = z.object({
  nombre: z.string().min(1, 'Requerido'),
  email: z.string().email('Email inválido'),
  rol: z.enum(['vendedor', 'admin', 'viewer']),
});

export default function UsuariosPage() {
  const { data: usuarios = [], isLoading } = useUsuarios();
  const [modal, setModal] = useState(null); // null | { modo: 'crear' } | { modo: 'editar', usuario } | { modo: 'reset', usuario }

  return (
    <div>
      <Header title="Usuarios" />
      <div className="p-6">
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setModal({ modo: 'crear' })}
            className="bg-[#1E9BE8] hover:bg-[#1780C5] text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px]"
          >
            + Nuevo usuario
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-100 shadow-sm">
          {isLoading ? (
            <p className="p-6 font-['Inter'] text-[#6B7280]">Cargando...</p>
          ) : (
            <table className="w-full text-sm font-['Inter']">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Nombre</th>
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Rol</th>
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Estado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <UsuarioRow
                    key={u.id}
                    usuario={u}
                    onEditar={() => setModal({ modo: 'editar', usuario: u })}
                    onReset={() => setModal({ modo: 'reset', usuario: u })}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && modal.modo !== 'reset' && (
        <ModalUsuario
          modal={modal}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.modo === 'reset' && (
        <ModalResetPassword
          usuario={modal.usuario}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function UsuarioRow({ usuario, onEditar, onReset }) {
  const toggle = useToggleActivo(usuario.id);

  return (
    <tr className="border-b border-gray-50">
      <td className="px-4 py-3 text-[#0D2B5C] font-medium">{usuario.nombre}</td>
      <td className="px-4 py-3 text-[#6B7280]">{usuario.email}</td>
      <td className="px-4 py-3">
        <RolBadge rol={usuario.rol} />
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          usuario.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {usuario.activo ? 'Activo' : 'Inactivo'}
        </span>
      </td>
      <td className="px-4 py-3 flex gap-3 justify-end">
        <button
          onClick={onEditar}
          className="text-[#0D2B5C] hover:text-[#1E9BE8] font-['Raleway'] font-semibold text-sm transition-colors"
        >
          Editar
        </button>
        <button
          onClick={onReset}
          className="text-[#6B7280] hover:text-[#0D2B5C] font-['Raleway'] text-sm transition-colors"
        >
          Resetear contraseña
        </button>
        <button
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending}
          className="text-[#6B7280] hover:text-[#0D2B5C] font-['Raleway'] text-sm transition-colors disabled:opacity-50"
        >
          {usuario.activo ? 'Desactivar' : 'Activar'}
        </button>
      </td>
    </tr>
  );
}

function RolBadge({ rol }) {
  const colores = {
    admin: 'bg-[#0D2B5C] text-white',
    vendedor: 'bg-blue-100 text-blue-700',
    viewer: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium font-['Inter'] ${colores[rol] ?? ''}`}>
      {rol}
    </span>
  );
}

function ModalUsuario({ modal, onClose }) {
  const esEditar = modal.modo === 'editar';
  const crear = useCreateUsuario();
  const editar = useUpdateUsuario(modal.usuario?.id);

  const schema = esEditar ? editarSchema : crearSchema;
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: esEditar ? {
      nombre: modal.usuario.nombre,
      email: modal.usuario.email,
      rol: modal.usuario.rol,
    } : { rol: 'vendedor' },
  });

  function onSubmit(data) {
    const mutation = esEditar ? editar : crear;
    mutation.mutate(data, { onSuccess: onClose });
  }

  const isPending = crear.isPending || editar.isPending;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4 flex flex-col gap-5">
        <h2 className="font-['Raleway'] font-bold text-[#0D2B5C] text-lg">
          {esEditar ? 'Editar usuario' : 'Nuevo usuario'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Campo label="Nombre" error={errors.nombre?.message}>
            <input {...register('nombre')} className={inputCls} placeholder="Juan García" />
          </Campo>

          <Campo label="Email" error={errors.email?.message}>
            <input {...register('email')} type="email" className={inputCls} placeholder="juan@isada.edu.ar" />
          </Campo>

          {!esEditar && (
            <Campo label="Contraseña" error={errors.password?.message}>
              <input {...register('password')} type="password" className={inputCls} placeholder="Mínimo 8 caracteres" />
            </Campo>
          )}

          <Campo label="Rol" error={errors.rol?.message}>
            <select {...register('rol')} className={inputCls}>
              <option value="vendedor">Vendedor</option>
              <option value="admin">Admin</option>
              <option value="viewer">Viewer (solo lectura)</option>
            </select>
          </Campo>

          {(crear.error || editar.error) && (
            <p className="text-red-500 text-sm font-['Inter']">
              {crear.error?.response?.data?.error || editar.error?.response?.data?.error || 'Error al guardar'}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isPending}
              className="flex-1 bg-[#1E9BE8] hover:bg-[#1780C5] text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px] disabled:opacity-50"
            >
              {isPending ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border-2 border-[#0D2B5C] text-[#0D2B5C] hover:bg-[#0D2B5C] hover:text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px]"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Campo({ label, error, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="font-['Raleway'] text-[#1E9BE8] text-xs font-semibold tracking-widest uppercase">
        {label}
      </label>
      {children}
      {error && <span className="text-red-500 text-xs font-['Inter']">{error}</span>}
    </div>
  );
}

const inputCls = "border border-gray-200 rounded-md px-3 py-2 text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C] w-full";

const resetPasswordSchema = z.object({
  password: z.string().min(8, 'Mínimo 8 caracteres'),
  confirmar: z.string().min(1, 'Requerido'),
}).refine((d) => d.password === d.confirmar, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmar'],
});

function ModalResetPassword({ usuario, onClose }) {
  const mutation = useResetPassword(usuario.id);
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(resetPasswordSchema),
  });

  function onSubmit({ password }) {
    mutation.mutate(password, { onSuccess: onClose });
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm mx-4 flex flex-col gap-5">
        <div>
          <h2 className="font-['Raleway'] font-bold text-[#0D2B5C] text-lg">Resetear contraseña</h2>
          <p className="text-sm text-[#6B7280] font-['Inter'] mt-1">{usuario.nombre}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Campo label="Nueva contraseña" error={errors.password?.message}>
            <input {...register('password')} type="password" className={inputCls} placeholder="Mínimo 8 caracteres" />
          </Campo>

          <Campo label="Confirmar contraseña" error={errors.confirmar?.message}>
            <input {...register('confirmar')} type="password" className={inputCls} />
          </Campo>

          {mutation.error && (
            <p className="text-red-500 text-sm font-['Inter']">
              {mutation.error?.response?.data?.error || 'Error al resetear contraseña'}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 bg-[#1E9BE8] hover:bg-[#1780C5] text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px] disabled:opacity-50"
            >
              {mutation.isPending ? 'Guardando...' : 'Resetear'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border-2 border-[#0D2B5C] text-[#0D2B5C] hover:bg-[#0D2B5C] hover:text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px]"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
